import { useEffect, useState } from "react";
import {
  useGetCompaniesInfoList,
  useLoadSettingTypes,
} from "../api/CompaniesApi";

import { useAppContext } from "../AppProviders";

type UserCompanySettings = {
  showChatSupport: boolean;
  consentForms: boolean;
  reviewCompletedReport: boolean;
};

const defaultUserCompanySettings: UserCompanySettings = {
  reviewCompletedReport: false,
  consentForms: false,
  showChatSupport: false,
};

export const useUserCompanySettings = () => {
  const [userCompanySettings, setUserCompanySettings] =
    useState<UserCompanySettings>({ ...defaultUserCompanySettings });
  const { companiesFromAuth } = useAppContext();
  const { data: settingTypes, mutate: loadSettingTypes } =
    useLoadSettingTypes();
  const { data: companyInfoList, mutate: loadCompanyInfoList } =
    useGetCompaniesInfoList();

  useEffect(() => {
    loadSettingTypes();
  }, [loadSettingTypes]);

  useEffect(() => {
    if (!companiesFromAuth || !settingTypes) return;
    loadCompanyInfoList(companiesFromAuth?.companyIds ?? []);
  }, [companiesFromAuth, loadCompanyInfoList, settingTypes]);

  useEffect(() => {
    if (companiesFromAuth?.allCompanies) {
      setUserCompanySettings({
        showChatSupport: true,
        consentForms: true,
        reviewCompletedReport: true,
      });
      return;
    }
    if (!settingTypes || !companyInfoList) return;
    const userCompanySettingsFull = companyInfoList?.map((companyInfo) => {
      const showChatSupport = companyInfo.chat_support;
      const reviewCompletedReport = companyInfo.review;
      const consentForms = companyInfo.consent_forms;
      const additionalWebSearch = companyInfo.additional_web_search;
      const webContentTranslation = companyInfo.web_content_translation;
      return {
        showChatSupport,
        reviewCompletedReport,
        consentForms,
        additionalWebSearch,
        webContentTranslation,
      };
    });

    const result = userCompanySettingsFull?.reduce<UserCompanySettings>(
      (prev, cur) => {
        return {
          showChatSupport: prev.showChatSupport || cur.showChatSupport,
          reviewCompletedReport:
            prev.reviewCompletedReport || cur.reviewCompletedReport,
          consentForms: prev.consentForms || cur.consentForms,
        };
      },
      userCompanySettingsFull[0]
    );
    setUserCompanySettings(result ?? { ...defaultUserCompanySettings });
  }, [settingTypes, companyInfoList, companiesFromAuth]);

  return userCompanySettings;
};
